import React, { useCallback, useState } from "react";
import { Modal, Button, Form, Input } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Http } from "../../../helpers/axois";
import { encodeBase64, useToast } from "../../../helpers/commonFunction";
import { ReactComponent as CloseIcon } from "../../../assets/svg/roundedCloseIcon.svg";
import { setFunctionData } from "../../../stateManager/store/marketplaceFunction/actions";
import { hasValue } from "../../../constant/utils";
import { logger } from "../../../constant/winston";
import { IPermissions, ExecutionMode } from './createFunction/types'
import UploadThumbnail from "../UploadThumbnail";
import {GCPENDPOINT} from "../../../constant/common";
import { isValidFuncName } from "../apps/helpers";
import { AppState } from "../../../stateManager/store";

interface IData {
  context: any;
  content: string;
  comSource: string;
  executionMode: ExecutionMode;
  permissions: IPermissions;
  metadata: any;
}

interface IProps {
  setVisible: any
  visible: boolean
  data: IData
}

export const SaveFunction: React.FC<IProps> = ({ 
  visible, 
  setVisible, 
  data
}) => {
  const {
    content,
    context,
    comSource,
    executionMode,
    permissions,
    metadata,
  } = data
  const editable = Boolean(metadata.id)
  const orgId = useSelector((state: any) => state.org.selectedOrg.id);
  const history = useHistory();
  const dispatch = useDispatch();
  const pathname = history.location.pathname;
  const functionId = pathname.split('/')[4];
  const [versionExist, setVersionExist] = useState<boolean>(false)
  const [isValidVersion, setIsValidVersion] = useState<boolean>(false)
  const [addFunction, setAddFunction] = useState<any>({
    name: editable ? metadata.name : "",
    description: editable ? metadata.description : "",
    currentVersion: editable ? metadata.current_version : "",
    releaseNotes: editable ? metadata.release_notes : "",
    authorWebsite: editable ? metadata.author_website : "",
    supportEmail: editable ? metadata.support_email : "",
    previewImage: (editable && metadata?.preview_image !== "" ) ? `${metadata.preview_image?.startsWith("https://") ? metadata.preview_image : GCPENDPOINT+metadata.preview_image}` : "",
    context: "",
  });
  const orgPermissions: any = useSelector((state: any) => {
    return state.org.orgPermissions
  }, (oldPermissions: any, newPermissions: any) => JSON.stringify(oldPermissions) === JSON.stringify(newPermissions))
  const re = new RegExp("^[0-9]+.[0-9]+.[0-9]+$");
  const { ErrorToast, SuccessToast } = useToast()
  const { selectedOrg } = useSelector((state: AppState) => state.org)
  const loggedInUserId = useSelector((state: any) => {
      return state.user.data.user.id
  })
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const data = {
      ...addFunction,
      context: encodeBase64(JSON.stringify(context)),
      source: encodeBase64(content),
      comSource: encodeBase64(comSource),
      permissions,
      executionMode
    }
    
    // does description satisfies the character count?
    const { description } = addFunction;
    if (description.length < 100 || description.length > 500) {
      return ErrorToast("Description should be between 100 and 500 characters")
    }

    // does the version number satisfies the format?
    const { currentVersion } = addFunction;
    if (!re.test(currentVersion)) {
      return ErrorToast("Enter a valid version number")
    }

    if (editable) {
      const endpoint = `/orgs/${orgId}/marketplaceFunctions/${functionId}`;
      try {
        await Http.put(endpoint, data).toPromise();
        SuccessToast("Function updated")
        setVisible(false);
        history.push(`/marketplace/functions`);
        getFunctions();
      } catch (e) {
        ErrorToast(`Failed to update Function`)
      }
    } else {
      const endpoint = `/orgs/${orgId}/marketplaceFunctions`;
      try {
        const resp: any = await Http.post(endpoint, data).toPromise();
        SuccessToast("Function created")
        setVisible(false);
        dispatch(setFunctionData(resp.product));
        getFunctions();
        history.push(`/marketplace/functions`);
      } catch (e) {
        ErrorToast(`Failed to create new Function`)
      }
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    if (name === "name" && !isValidFuncName(value)) {
      return;
    }
    setAddFunction((prevState: any) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleOnCancel = (): void => {
    setVisible(false);
  };

  const handleOnBlur = () => {
    if (editable) {
      const version = addFunction.currentVersion <= metadata.current_version
      setVersionExist(version)
    } else {
      setVersionExist(false)
    }
    if (!re.test(addFunction.currentVersion)) {
      setIsValidVersion(true)
    } else {
      setIsValidVersion(false)
    }
  }

  const getFunctions = useCallback(() => {
    if ((!orgPermissions?.includes("marketplace.PublishFunc") || !orgPermissions.includes('org.View')) && loggedInUserId!=selectedOrg?.owner) {
      ErrorToast("Not Authorized: Error fetching functions")
      return
    } 
    Http.get(
      `/orgs/${orgId}/marketplaceFunctions`
    ).subscribe((res: any) => {
      if (res) {
        dispatch(setFunctionData(res.data))
      }
    }, (e) => {
      if (e === "Forbidden") {
        ErrorToast("Not Authorized: Error fetching functions")
      }
      logger.error(e);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSetState = (key: string, value: any, isArray = false) => {
    setAddFunction((prevState: any) => {
      let updatedValue = prevState[key];

      if (isArray) {
        updatedValue = [...updatedValue, value];
      }

      return {
        ...prevState,
        [key]: isArray ? updatedValue : value,
      };
    });
  };

  const isVersionExist = metadata.current_version === addFunction.currentVersion;
  return (
    <div>
      <Modal
        keyboard={true}
        title={editable ? "EDIT FUNCTION" : "ADD FUNCTION"}
        visible={visible}
        className="override-modal"
        style={{
          top: 100,
        }}
        width="700px"
        closeIcon={
          <CloseIcon
            data-test-id="close_icon"
            height="18px"
            width="18px"
            onClick={() => setVisible(false)}
          />
        }
        onCancel={() => {
          setVisible(false);
          let modalId: number = 7238216;
          let event = new KeyboardEvent("escape", {
            key: "Escape",
            code: "KeyS",
            detail: modalId,
          });
          document.dispatchEvent(event);
        }}
        footer={[
          <div className="version-footer">
            <Button type="default" className="cancel" onClick={handleOnCancel}>
              Cancel
            </Button>
            <Button disabled={versionExist || !re.test(addFunction.currentVersion) || isVersionExist || !isValidFuncName(addFunction.name)} type="primary" className="submit" onClick={handleSubmit}>
              Save
            </Button>
          </div>,
        ]}
        maskStyle={{
          background: "#0000001",
        }}
      >
        <Form className="version-modal">
          <Form.Item required >
            <UploadThumbnail setThumbnail={handleSetState} info={addFunction} setInfo={setAddFunction}  orgId={orgId} fromFunction={true}  />
          </Form.Item>
          <Form.Item label="Enter Name" className="col-md-12" required>
            <Input
              data-test-id="function_name"
              name="name"
              disabled={editable}
              value={editable ? metadata.name : addFunction.name}
              placeholder="Enter Function Name"
              onChange={handleInputChange}
              maxLength={35}
            />
            {!editable && hasValue(addFunction.name) &&
              <p style={{ marginTop: "10px", color: "#3461A2" }}>
                {addFunction.name.length >= 200 ? "Name should be less than 35 characters" : `Remaining characters: ${35 - addFunction.name.length}`}
              </p>
            }
          </Form.Item>
          <Form.Item label="Description" className="col-md-12" required>
            <Input.TextArea
              data-test-id="function_description"
              key="name"
              name="description"
              autoSize={{ minRows: 5 }}
              placeholder="Enter Funcion Description"
              value={addFunction.description}
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item label="Version" className="col-md-12" required>
            <Input
              data-test-id="function_version"
              key="currentVersion"
              name="currentVersion"
              placeholder="Enter Version Number"
              value={addFunction.currentVersion}
              onChange={handleInputChange}
              onBlur={handleOnBlur}
            />
            {versionExist &&
              <div className="text-danger p-2 mt-3">Version should be greater than existing version</div>
            }
            {isValidVersion &&
              <div className="text-danger p-2 mt-3">Enter a valid version number</div>
            }
          </Form.Item>
          <Form.Item label="Release Notes" className="col-md-12">
            <Input
              data-test-id="release_notes"
              key="releaseNotes"
              name="releaseNotes"
              placeholder="Release Notes"
              value={addFunction.releaseNotes}
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item label="Support Email" className="col-md-12">
            <Input
              data-test-id="support_email"
              key="supportEmail"
              name="supportEmail"
              placeholder="Enter Email"
              value={addFunction.supportEmail}
              onChange={handleInputChange}
            />
          </Form.Item>
          <Form.Item label="Author Website" className="col-md-12">
            <Input
              data-test-id="function_version"
              key="authorWebsite"
              name="authorWebsite"
              placeholder="Enter Website"
              value={addFunction.authorWebsite}
              onChange={handleInputChange}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
